import type { MessageSchedule } from '@teo/widget-shared';

type ScheduleSlot = { start: string; end: string };

/** ISO weekday: 1 = Monday … 7 = Sunday */
function getIsoWeekdayKey(date: Date): string {
  const day = date.getDay();
  return day === 0 ? '7' : String(day);
}

function parseTimeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map((part) => Number.parseInt(part, 10));
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0;
  return hours * 60 + minutes;
}

function isAlwaysAvailableSlot(slot: ScheduleSlot): boolean {
  const start = slot.start?.trim() || '00:00';
  const end = slot.end?.trim() || '00:00';
  return start === '00:00' && (end === '00:00' || end === '23:59');
}

function isMinuteWithinSlot(minutes: number, slot: ScheduleSlot): boolean {
  if (isAlwaysAvailableSlot(slot)) return true;

  const start = parseTimeToMinutes(slot.start || '00:00');
  const end = parseTimeToMinutes(slot.end || '23:59');

  if (start <= end) {
    return minutes >= start && minutes <= end;
  }

  return minutes >= start || minutes <= end;
}

/**
 * Returns whether the widget should be visible now per `MESSAGE_SCHEDULE`.
 * Empty schedule → always available.
 */
export function isWithinSchedule(
  schedule: MessageSchedule,
  now: Date = new Date(),
): boolean {
  if (!schedule || Object.keys(schedule).length === 0) return true;

  const dayKey = getIsoWeekdayKey(now);
  const slots = schedule[dayKey];
  if (!slots?.length) return false;

  const minutes = now.getHours() * 60 + now.getMinutes();
  return slots.some((slot) => isMinuteWithinSlot(minutes, slot));
}
